import { Checkbox, Divider, Group, SimpleGrid, Stack, Text } from "@mantine/core";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { toggleProsjektinfoAndApplyRules } from "./akustikkRapportThunks";

export const AkustikkProsjektinfo: React.FC = () => {
  const dispatch = useAppDispatch();
  const edited = useAppSelector((state) => state.reportReducer.editedData[401]);
  if (!edited) return null;

  const p = edited.prosjektinfo;

  const Row: React.FC<{
    label: string;
    checked: boolean;
    path: string;
    indent?: number;
    disabled?: boolean;
  }> = ({ label, checked, path, indent = 0, disabled }) => (
    <Group gap="sm" align="center" style={{ paddingLeft: indent }} wrap="nowrap">
      <Checkbox
        size="sm"
        checked={checked}
        disabled={disabled}
        onChange={(e) => dispatch(toggleProsjektinfoAndApplyRules(path, e.currentTarget.checked))}
      />
      <Text size="sm" style={{ flex: 1 }}>
        {label}
      </Text>
    </Group>
  );

  const RowsGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
      {children}
    </SimpleGrid>
  );

  return (
    <Stack>
      <Divider
        mt="sm"
        label={
          <Text fz="md" fw={600}>
            Prosjektinfo
          </Text>
        }
      />

      {/* Fase */}
      <Divider
        label={
          <Text fz="sm" fw={600}>
            Fase
          </Text>
        }
      />
      <RowsGrid>
        <Row
          label={p.fase.skisseForprosjekt.text}
          checked={p.fase.skisseForprosjekt.isActive}
          path="prosjektinfo.fase.skisseForprosjekt.isActive"
        />
        <Row
          label={p.fase.totalenterprisegrunnlag.text}
          checked={p.fase.totalenterprisegrunnlag.isActive}
          path="prosjektinfo.fase.totalenterprisegrunnlag.isActive"
        />
        <Row
          label={p.fase.detaljprosjekt.text}
          checked={p.fase.detaljprosjekt.isActive}
          path="prosjektinfo.fase.detaljprosjekt.isActive"
        />
        <Row
          label={p.fase.rehabilitering.text}
          checked={p.fase.rehabilitering.isActive}
          path="prosjektinfo.fase.rehabilitering.isActive"
        />
      </RowsGrid>

      {/* Vedlegg */}
      <Divider
        label={
          <Text fz="sm" fw={600}>
            Vedlegg
          </Text>
        }
      />
      <Stack gap="xs">
        <Row
          label={p.vedlegg?.lydtegninger.text}
          checked={p.vedlegg?.lydtegninger.isActive}
          path="prosjektinfo.vedlegg.lydtegninger.isActive"
        />
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {p.vedlegg?.tabellEllerTekst.text}
          </Text>
          <Stack gap="xs" pl={28}>
            <Row
              label={p.vedlegg?.tabellEllerTekst.tabellGrenseverdier.text}
              checked={p.vedlegg?.tabellEllerTekst.tabellGrenseverdier.isActive}
              path="prosjektinfo.vedlegg.tabellEllerTekst.tabellGrenseverdier.isActive"
            />
            <Row
              label={p.vedlegg?.tabellEllerTekst.henvisningVedlegg.text}
              checked={p.vedlegg?.tabellEllerTekst.henvisningVedlegg.isActive}
              path="prosjektinfo.vedlegg.tabellEllerTekst.henvisningVedlegg.isActive"
            />
          </Stack>
        </Stack>
      </Stack>

      {/* Type bygg */}
      <Divider
        label={
          <Text fz="sm" fw={600}>
            Type bygg
          </Text>
        }
      />
      <RowsGrid>
        <Row
          label={p.typeBygg.bolig.text}
          checked={p.typeBygg.bolig.isActive}
          path="prosjektinfo.typeBygg.bolig.isActive"
        />
        <Row
          label={p.typeBygg.undervisningsbygg.text}
          checked={p.typeBygg.undervisningsbygg.isActive}
          path="prosjektinfo.typeBygg.undervisningsbygg.isActive"
        />
        <Row
          label={p.typeBygg.bolig.flerFamiliehus.text}
          checked={p.typeBygg.bolig.flerFamiliehus.isActive}
          path="prosjektinfo.typeBygg.bolig.flerFamiliehus.isActive"
          indent={18}
          disabled={!p.typeBygg.bolig.isActive}
        />
        <Row
          label={p.typeBygg.barnehage.text}
          checked={p.typeBygg.barnehage.isActive}
          path="prosjektinfo.typeBygg.barnehage.isActive"
        />
        <Row
          label={p.typeBygg.helsebygg.text}
          checked={p.typeBygg.helsebygg.isActive}
          path="prosjektinfo.typeBygg.helsebygg.isActive"
        />
        <Row
          label={p.typeBygg.omsorgsboliger.text}
          checked={p.typeBygg.omsorgsboliger.isActive}
          path="prosjektinfo.typeBygg.omsorgsboliger.isActive"
        />
        <Row
          label={p.typeBygg.hotell.text}
          checked={p.typeBygg.hotell.isActive}
          path="prosjektinfo.typeBygg.hotell.isActive"
        />
        <Row
          label={p.typeBygg.kontorbygg.text}
          checked={p.typeBygg.kontorbygg.isActive}
          path="prosjektinfo.typeBygg.kontorbygg.isActive"
        />
        <Row
          label={p.typeBygg.kulturbygg.text}
          checked={p.typeBygg.kulturbygg.isActive}
          path="prosjektinfo.typeBygg.kulturbygg.isActive"
        />
        <Row
          label={p.typeBygg.idrettsbygg.text}
          checked={p.typeBygg.idrettsbygg.isActive}
          path="prosjektinfo.typeBygg.idrettsbygg.isActive"
        />
        <Row
          label={p.typeBygg.butikk.text}
          checked={p.typeBygg.butikk.isActive}
          path="prosjektinfo.typeBygg.butikk.isActive"
        />
        <Row
          label={p.typeBygg.produksjonsbygg.text}
          checked={p.typeBygg.produksjonsbygg.isActive}
          path="prosjektinfo.typeBygg.produksjonsbygg.isActive"
        />
        <Row
          label={p.typeBygg.restaurant.text}
          checked={p.typeBygg.restaurant.isActive}
          path="prosjektinfo.typeBygg.restaurant.isActive"
        />
        <Row
          label={p.typeBygg.treningsrom.text}
          checked={p.typeBygg.treningsrom.isActive}
          path="prosjektinfo.typeBygg.treningsrom.isActive"
        />
      </RowsGrid>

      {/* Konstruksjonstype */}
      <Divider
        label={
          <Text fz="sm" fw={600}>
            Konstruksjonstype
          </Text>
        }
      />
      <RowsGrid>
        <Row
          label={p.konstruksjonstype.lette.text}
          checked={p.konstruksjonstype.lette.isActive}
          path="prosjektinfo.konstruksjonstype.lette.isActive"
        />
        <Row
          label={p.konstruksjonstype.tunge.text}
          checked={p.konstruksjonstype.tunge.isActive}
          path="prosjektinfo.konstruksjonstype.tunge.isActive"
        />
        <Row
          label={p.konstruksjonstype.massivtre.text}
          checked={p.konstruksjonstype.massivtre.isActive}
          path="prosjektinfo.konstruksjonstype.massivtre.isActive"
        />
        <Row
          label={p.konstruksjonstype.heis.text}
          checked={p.konstruksjonstype.heis.isActive}
          path="prosjektinfo.konstruksjonstype.heis.isActive"
        />
      </RowsGrid>
    </Stack>
  );
};
